/* eslint-disable react/jsx-wrap-multilines */
import React, { useRef, useState, useMemo, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  makeStyles,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Box,
} from '@material-ui/core';
import PropTypes from 'prop-types';
import { useQuery, useMutation } from 'react-query';
import { useParams } from 'react-router-dom';
import { useForm } from '@omega-energia/react';
import cls from 'classnames';
import { AgenteAnaliseCreditoStatus } from '@omega-energia/utilities';
import { isEqual } from 'lodash';
import req from '../../../../../services/network/request';
import { useAuth } from '../../../../../auth/authProvider';
import { formatCurrency } from '../../../../../helpers';
import styles from './AnaliseConclusaoDialog.style';
import { NumberFormatCustom } from '../../../NumberFormatCustom';
import SimulacaoFaturamento from '../../../SimulacaoFaturamento/SimulacaoFaturamento';

const useStyles = makeStyles(styles);

function AnaliseConclusaoDialog(props) {
  const { onClose, dadosAnalise, canEdit } = props;

  const classes = useStyles(props);
  const { analiseId } = useParams();
  const { token } = useAuth();

  const scrollTableRef = useRef(undefined);

  const [concluida, setConcluida] = useState(false);
  const [dialogConfirmation, setDialogConfirmation] = useState({ open: false, formData: null });
  const [dialogForm, setDialogForm] = useState(!concluida && !dialogConfirmation.open);
  const [firstTimeScroll, setFirstTimeScroll] = useState(true);
  const [initialData] = useState(() => {
    const { comentario, credito, pontuacao, rating } = dadosAnalise;
    return { comentario, credito, pontuacao, rating };
  });
  const { data: rating, isFetchingRating } = useQuery('fetch-rating', () =>
    req.get(`/credito/analise/rating`, { token }),
  );

  const [saveAnalise] = useMutation(({ id, ...postData }) => req.put(`/credito/analise/${id}`, postData, { token }), {
    onSuccess: () => {
      setConcluida(true);
    },
  });

  const [inputs, handleChange, handleSubmit, , setInputs] = useForm(
    {
      credito: '',
      creditoCustom: '',
      comentario: '',
      pontuacao: undefined,
      rating: undefined,
      option: '',
    },
    formData => {
      let creditoCustom = null;

      if (formData.creditoCustom?.trim() !== '') {
        creditoCustom = formData.creditoCustom.replace(/[^0-9]/g, '');
        delete formData.credito;
      }

      if (formData.reprovada) {
        setDialogConfirmation({ open: true, formData });
        setDialogForm(false);
      } else {
        saveAnalise({
          id: analiseId,
          credito: parseInt(formData.credito ?? creditoCustom, 10),
          pontuacao: parseFloat(formData.pontuacao),
          rating: formData.rating,
          comentario: formData.comentario,
          status: AgenteAnaliseCreditoStatus.ANALISE_CONCLUIDA,
        });
      }
    },
  );

  const canUpdateData = useMemo(() => {
    return !isEqual(initialData, {
      comentario: inputs.comentario,
      credito: parseInt(inputs.credito, 10),
      pontuacao: inputs.pontuacao,
      rating: inputs.rating,
    });
  }, [initialData, inputs]);

  const handleCloseDialogConfirmation = arg => () => {
    setDialogConfirmation(prevState => ({ ...prevState, open: false }));

    if (arg === 'confirm') {
      setDialogForm(false);
      saveAnalise({
        id: analiseId,
        status: AgenteAnaliseCreditoStatus.REPROVADA,
        comentario: dialogConfirmation.formData.comentario,
      });
    } else if (arg === 'cancel') {
      setDialogForm(true);
    }
  };

  const creditOption = data => {
    if (data?.limite && !data.score && !data.rating) {
      return 'custom';
    } else {
      return data.rating;
    }
  };
  const { data: score, isFetchingScore } = useQuery(
    [canEdit === false && 'fetch-score'],
    () => req.get(`/credito/analise/score/${analiseId}`, { token }),
    {
      enabled: !canEdit,
      onSuccess: data => {
        if (data?.score && data?.rating) {
          setInputs({
            credito: creditOption(data) === 'custom' ? '' : data?.limite ?? '',
            creditoCustom: creditOption(data) === 'custom' ? data?.limite ?? '' : '',
            comentario: dadosAnalise.comentario,
            pontuacao: data?.score ?? undefined,
            rating: data?.rating ?? undefined,
            option: creditOption(data),
          });
        }
      },
    },
  );

  const isResolvedScore = useMutation(() => (!canEdit && isFetchingScore && score) || canEdit);

  useEffect(() => {
    if (canEdit) {
      setInputs({
        credito: creditOption(dadosAnalise) === 'custom' ? '' : dadosAnalise?.credito ?? '',
        creditoCustom: creditOption(dadosAnalise) === 'custom' ? dadosAnalise?.credito ?? '' : '',
        comentario: dadosAnalise.comentario,
        pontuacao: dadosAnalise?.pontuacao ?? undefined,
        rating: dadosAnalise?.rating ?? undefined,
        option: creditOption(dadosAnalise),
      });
    }
  }, [canEdit, setInputs, dadosAnalise]);

  const formatRatingLabel = label => {
    return label
      .replace('_MAIS', '+')
      .replace('_MENOS', '-')
      .replace('_MAIS_MENOS', '+/-');
  };

  const onFocusResultadoAnalise = tipo => {
    if (tipo === 'custom') {
      setInputs(prev => ({
        ...prev,
        option: 'custom',
        credito: '',
        rating: undefined,
        pontuacao: undefined,
        reprovada: false,
      }));
    } else if (tipo === 'reprovada') {
      setInputs(prev => ({
        ...prev,
        option: 'reprovada',
        credito: '',
        creditoCustom: '',
        rating: undefined,
        pontuacao: undefined,
        reprovada: true,
      }));
    } else {
      const value = rating.find(item => item.rating === tipo);
      setInputs(prev => ({
        ...prev,
        option: value.rating,
        credito: value.limite.toString(),
        creditoCustom: '',
        rating: value.rating,
        pontuacao: value.pontos,
        reprovada: false,
      }));
    }
  };

  const canSubmit = useMemo(() => {
    return (inputs.credito || inputs.creditoCustom || inputs.reprovada) && inputs.comentario && canUpdateData;
  }, [inputs, canUpdateData]);

  return (
    <>
      <Dialog open={concluida} className={classes.dialog}>
        <DialogTitle>
          <Typography display="inline" className={classes.dialogTitle}>
            Análise Concluída
          </Typography>
        </DialogTitle>
        <DialogContent style={{ width: '280px', height: '10px' }} />
        <DialogActions>
          <Button color="primary" onClick={() => onClose(concluida)}>
            ENTENDI
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialogForm} maxWidth="md" fullWidth className={classes.dialog}>
        <form onChange={handleChange} onSubmit={handleSubmit}>
          <DialogTitle>
            <Typography color="primary">
              {canEdit ? 'Editar análise de crédito ' : 'Análise de crédito '}
              <Typography component="span" display="inline" className={classes.dialogSubtitle}>
                {dadosAnalise.empresa ? dadosAnalise.empresa.nome : dadosAnalise.controladora.nome}
              </Typography>
            </Typography>
          </DialogTitle>
          <Box pl={3} pb={3} pr="20px">
            {dadosAnalise.empresa && <SimulacaoFaturamento empresaId={dadosAnalise.empresa.id} />}
          </Box>
          <DialogContent style={{ overflow: 'hidden' }}>
            {!isFetchingRating && rating && isResolvedScore && (
              <Grid container justify="flex-start" spacing={4}>
                <Grid item xs={6}>
                  <RadioGroup name="option" value={inputs.option} color="primary">
                    <Table width="100%">
                      <TableHead className={classes.tableHead}>
                        <TableRow>
                          <TableCell align="center" padding="none" width="10%" />
                          <TableCell width="24%" align="center">
                            Score
                          </TableCell>
                          <TableCell width="38%" align="center">
                            Rating S&P
                          </TableCell>
                          <TableCell width="38%" align="center">
                            Limite
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody className={classes.tableBody} ref={scrollTableRef}>
                        {rating?.map((item, index) => {
                          return (
                            <TableRow
                              key={item.rating}
                              className={cls(classes.tableRow, inputs.option === item.rating ? classes.bgBlue : '')}
                            >
                              <TableCell align="center" padding="none">
                                <FormControlLabel
                                  value={item.rating}
                                  ref={elm => {
                                    if (
                                      inputs.option === item.rating &&
                                      elm?.scrollIntoView &&
                                      firstTimeScroll === true
                                    ) {
                                      elm.scrollIntoView(false);

                                      const size = 1;

                                      if (scrollTableRef.current && index < size) {
                                        scrollTableRef.current.scrollBy({ top: -64 });
                                      }

                                      if (scrollTableRef.current && index > size) {
                                        scrollTableRef.current.scrollBy({ top: 64 });
                                      }

                                      setFirstTimeScroll(false);
                                    }
                                  }}
                                  control={
                                    <Radio
                                      color="primary"
                                      onFocus={() => {
                                        onFocusResultadoAnalise(item.rating);
                                      }}
                                    />
                                  }
                                  className={classes.formControlLabel}
                                />
                              </TableCell>
                              <TableCell align="center" width="24%">
                                <Typography className={classes.grey}>{item.pontos}</Typography>
                              </TableCell>
                              <TableCell align="center" width="38%">
                                <Typography className={classes.grey}>{formatRatingLabel(item.rating)}</Typography>
                              </TableCell>
                              <TableCell align="center" width="38%">
                                <Typography className={classes.grey} style={{ whiteSpace: 'nowrap' }}>
                                  {formatCurrency(item.limite)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>

                    <FormControlLabel
                      value="custom"
                      control={
                        <Radio
                          color="primary"
                          onFocus={() => {
                            onFocusResultadoAnalise('custom');
                          }}
                        />
                      }
                      label={
                        <span style={{ lineHeight: '56px' }}>
                          <TextField
                            value={inputs.creditoCustom}
                            name="creditoCustom"
                            label="Aprovar outro limite"
                            variant="outlined"
                            className={classes.inputCustom}
                            onFocus={() => {
                              onFocusResultadoAnalise('custom');
                            }}
                            required={inputs.option === 'custom'}
                            InputProps={{
                              inputComponent: NumberFormatCustom,
                            }}
                          />
                        </span>
                      }
                      className={classes.formControlLabel}
                    />

                    <FormControlLabel
                      value="reprovada"
                      control={
                        <Radio
                          color="primary"
                          onFocus={() => {
                            onFocusResultadoAnalise('reprovada');
                          }}
                        />
                      }
                      label={<span style={{ lineHeight: '56px' }}>Reprovar organização por crédito</span>}
                      className={classes.formControlLabel}
                    />
                  </RadioGroup>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    name="comentario"
                    value={inputs.comentario}
                    label="Comentário"
                    multiline
                    rows={12}
                    variant="outlined"
                    className={classes.inputComentario}
                    required
                  />
                </Grid>
              </Grid>
            )}
            {isFetchingRating ||
              (isFetchingScore && (
                <div className={classes.progressWrapper} align="center">
                  <CircularProgress />
                </div>
              ))}
          </DialogContent>
          <DialogActions>
            <Button color="primary" onClick={() => onClose(concluida)}>
              CANCELAR
            </Button>
            <Button type="submit" color="primary" disabled={!canSubmit}>
              CONCLUIR
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={dialogConfirmation.open} maxWidth="xs">
        <DialogTitle>
          <Typography>Atenção</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography color="textSecondary" gutterBottom>
            Tem certeza de que deseja reprovar
            <br /> esse cadastro?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={handleCloseDialogConfirmation('cancel')}>
            Cancelar
          </Button>
          <Button color="primary" onClick={handleCloseDialogConfirmation('confirm')}>
            Sim, reprovar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

AnaliseConclusaoDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  canEdit: PropTypes.bool.isRequired,
  dadosAnalise: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
    nome: PropTypes.string,
    credito: PropTypes.number,
    pontuacao: PropTypes.string,
    comentario: PropTypes.string,
    rating: PropTypes.string,
    periodo: PropTypes.array,
    empresa: PropTypes.shape({
      nome: PropTypes.string,
      id: PropTypes.string,
    }),
    controladora: PropTypes.object,
  }).isRequired,
};

export default AnaliseConclusaoDialog;
